import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import type { WorkEnvironmentSurvey } from '../../api/workEnvironmentTypes';
import { useWorkEnvironmentRiskQuery } from '../../hooks/queries/useWorkEnvironmentRiskQuery';
import { useWorkEnvironmentSurveyProgressQuery } from '../../hooks/queries/useWorkEnvironmentSurveyProgress';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { ActivityCard } from './components/ActivityCard';
import { AssessmentCard } from './components/AssessmentCard';
import { DataStepCard } from './components/DataStepCard';
import { ReportCard } from './components/ReportCard';
import { InsufficientCard } from './components/InsufficientCard';
import { InNumbersCard } from './components/InNumbersCard';
import { ProcessHero } from './components/ProcessHero';
import { RiskMatrixCard } from './components/RiskMatrixCard';
import { RiskStatusCard } from './components/RiskStatusCard';
import { activityLog, processStepIndex, riskHeadline, riskStepText } from './riskView';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';
import { ShareCard } from './components/ShareCard';
import { sharesResult, teamPageLink } from './sharingView';

/** The survey has closed. The team leader, HR and the safety representative see the risk. */
export const WorkEnvironmentSurveyRisk = ({ survey }: { survey: WorkEnvironmentSurvey }): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useWorkEnvironmentRiskQuery(survey.id, questionLanguageFor(i18n.language));
  const { data: progress } = useWorkEnvironmentSurveyProgressQuery(survey.id);
  const team = survey.teamName;
  const calculated = data?.calculatedAt ? data : null;
  const risk = calculated?.risk ?? null;
  const headline = riskHeadline(risk, t);

  return (
    <div data-size="md">
      <ProcessHero
        title={survey.title}
        intro={
          !progress
            ? t('workEnvironmentProcess.introWithoutDeadline', { team })
            : progress.responses >= progress.expected
              ? t('workEnvironmentRisk.intro.all', { team, count: progress.responses })
              : t('workEnvironmentRisk.intro.some', { team, ...progress })
        }
        tag={headline}
        process={{
          current: processStepIndex(survey),
          createdAt: survey.createdAt,
          responseDeadline: progress?.responseDeadline ?? null,
          riskStepText: riskStepText(calculated, t, i18n.language),
        }}
      />
      <div className={process.layout}>
        <div className={classes.mainColumn}>
          {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
          {isError && (
            <Alert data-color="danger">
              <Paragraph>{t('workEnvironmentRisk.error')}</Paragraph>
            </Alert>
          )}
          {data && !calculated && <RiskStatusCard />}
          {calculated?.risk.status === 'insufficientResponses' && calculated.insufficient && (
            <InsufficientCard surveyId={survey.id} risk={calculated.risk} insufficient={calculated.insufficient} />
          )}
          {calculated?.risk.status === 'assessed' && calculated.dimensions && (
            <>
              {calculated.assessment && (
                <AssessmentCard
                  surveyId={survey.id}
                  calculatedAt={calculated.calculatedAt}
                  areas={calculated.risk.areas}
                  levels={calculated.risk.levels}
                  assessment={calculated.assessment}
                />
              )}
              {calculated.dataSources?.canChange && (
                <DataStepCard
                  surveyId={survey.id}
                  areas={calculated.risk.areas}
                  sources={calculated.dataSources.items}
                />
              )}
              <RiskMatrixCard risk={calculated.risk} sources={calculated.dataSources?.items ?? []} />
              <InNumbersCard areas={calculated.risk.areas} dimensions={calculated.dimensions} />
            </>
          )}
        </div>
        {data && (
          <div className={process.side}>
            {calculated?.risk.status === 'assessed' && sharesResult(survey.status) && (
              <ShareCard
                link={teamPageLink(window.location.origin, survey.uuid)}
                text={t('workEnvironmentSharing.result')}
                linkLabel={t('workEnvironmentSharing.teamPage')}
                sharing={{ surveyId: survey.id, teamId: survey.teamId, kind: 'RESULT' }}
              />
            )}
            {calculated?.risk.status === 'assessed' && <ReportCard surveyId={survey.id} />}
            <ActivityCard entries={activityLog(data, progress, survey.createdAt)} />
          </div>
        )}
      </div>
    </div>
  );
};
